import { tokens } from './tokens';

// "#RRGGBB" → "rgba(r, g, b, alpha)"
export const hexToRgba = (hex, alpha) => {
  const [r, g, b] = hex.replace('#', '').match(/../g).map((x) => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Per-colour semantics (both modes):
//   main             fill colour for buttons, filled chips, icons
//   light            lighter shade of main (still saturated; hover fills, borders)
//   dark             darker shade of main (hover of filled elements)
//   lighter          soft tint for backgrounds (banners, selected rows, soft chips)
//   contrastText     text on `main` / `dark`
//   contrastTextDark text on `lighter` / tinted backgrounds
export const palette = (mode) => {
  const colors = tokens(mode);
  const isDark = mode === 'dark';

  // In light mode the ramps are inverted (see tokens.js), so the same index
  // yields "strong" in both modes.
  const accent = (ramp, tint, onTint) => (isDark
    ? {
      main: ramp[400],
      light: ramp[300],
      dark: ramp[500],
      lighter: tint,
      contrastText: colors.grey[1000],
      contrastTextDark: onTint,
    }
    : {
      main: ramp[300],
      light: ramp[400],
      dark: ramp[200],
      lighter: tint,
      contrastText: '#FFFFFF',
      contrastTextDark: onTint,
    });

  const text = {
    primary: colors.grey[100],
    secondary: isDark ? colors.grey[300] : colors.grey[200],
    tertiary: isDark ? colors.grey[400] : colors.grey[300],
    disabled: colors.grey[500],
    contrast: colors.grey[1000],
  };

  const background = isDark
    ? { default: colors.grey[1000], paper: colors.grey[900], alternate: colors.grey[800] }
    : { default: colors.grey[900], paper: colors.grey[1000], alternate: colors.grey[800] };

  return {
    mode,
    primary: isDark
      ? accent(colors.primary, '#1A2D4A', colors.primary[200])
      : {
        // Light mode primary sits one step deeper so white text clears 4.5:1
        main: colors.primary[400],
        light: colors.primary[500],
        dark: colors.primary[300],
        lighter: colors.primary[900],
        contrastText: '#FFFFFF',
        contrastTextDark: colors.primary[200],
      },
    // Violet: pink read as alarming maroon when used for everyday states ("Afventende")
    secondary: isDark
      ? { main: '#A78BFA', light: '#C4B5FD', dark: '#8B5CF6', lighter: '#2E1F5E', contrastText: colors.grey[1000], contrastTextDark: '#DDD6FE' }
      : { main: '#7C3AED', light: '#8B5CF6', dark: '#6D28D9', lighter: '#EDE9FE', contrastText: '#FFFFFF', contrastTextDark: '#5B21B6' },
    error: isDark
      ? accent(colors.redAccent, '#4C1717', colors.redAccent[200])
      : { ...accent(colors.redAccent, colors.redAccent[900], colors.redAccent[200]), main: colors.redAccent[400], light: colors.redAccent[500], dark: colors.redAccent[300] },
    // Orange rather than amber: amber dark enough for white text turns muddy brown
    warning: isDark
      ? { main: '#FB923C', light: '#FDBA74', dark: '#F97316', lighter: '#431407', contrastText: colors.grey[1000], contrastTextDark: '#FED7AA', background: '#431407' }
      : { main: '#C2410C', light: '#EA580C', dark: '#9A3412', lighter: '#FFEDD5', contrastText: '#FFFFFF', contrastTextDark: '#9A3412', background: '#FFEDD5' },
    info: isDark
      ? accent(colors.blueAccent, '#0C3A52', colors.blueAccent[200])
      : accent(colors.blueAccent, colors.blueAccent[900], colors.blueAccent[200]),
    success: accent(colors.greenAccent, colors.greenAccent[900], colors.greenAccent[200]),
    // MUI convention: grey[50] lightest … grey[900] darkest, in both modes.
    grey: isDark
      ? {
        50: colors.grey[100],
        100: colors.grey[200],
        200: colors.grey[300],
        300: colors.grey[400],
        400: colors.grey[500],
        500: colors.grey[600],
        600: colors.grey[700],
        700: colors.grey[800],
        800: colors.grey[900],
        900: colors.grey[1000],
        A100: colors.grey[200],
        A200: colors.grey[400],
        A400: colors.grey[600],
        A700: colors.grey[800],
        main: colors.grey[500],
        light: colors.grey[300],
        dark: colors.grey[700],
        contrastText: colors.grey[100],
      }
      : {
        50: colors.grey[900],
        100: colors.grey[800],
        200: colors.grey[700],
        300: colors.grey[600],
        400: colors.grey[500],
        500: colors.grey[400],
        600: colors.grey[300],
        700: colors.grey[200],
        800: colors.grey[100],
        900: colors.grey[100],
        A100: colors.grey[700],
        A200: colors.grey[500],
        A400: colors.grey[300],
        A700: colors.grey[200],
        main: colors.grey[500],
        light: colors.grey[700],
        dark: colors.grey[300],
        contrastText: colors.grey[100],
      },
    text,
    neutral: {
      dark: colors.grey[700],
      main: colors.grey[500],
      light: colors.grey[100],
    },
    background,
    divider: colors.grey[700],
    border: {
      strong: colors.grey[600],
    },
    action: {
      active: colors.grey[200],
      hover: hexToRgba(colors.grey[100], isDark ? 0.06 : 0.04),
      selected: hexToRgba(colors.primary[400], isDark ? 0.16 : 0.1),
      disabled: colors.grey[500],
      disabledBackground: hexToRgba(colors.grey[100], 0.12),
      focus: hexToRgba(colors.primary[400], 0.2),
    },
  };
};
